import { FileText, Paperclip, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CareCommunicationStatus } from '@/hooks/useCareCommunications';
import { CareCommunicationPriorityChip } from '@/components/medcom/CareCommunicationPriorityChip';
import { OrganizationLabel, OrganizationName } from '@/components/medcom/OrganizationName';
import { organizationOnLetter } from '@/lib/medcom/letterDisplay';
import {
  episodeOfCareLabel,
  formatMedcomDateTime,
  type BundleAttachment,
  type BundleSignature,
  type ParsedBundleContent,
} from '@/lib/medcom/parseCareCommunicationBundle';
import {
  receiptStatusClass,
  type AcknowledgementCard,
  type ReceiptStatusTone,
} from '@/pages/medcom/acknowledgementDisplay';
import { formatDanishDateTime } from '@/lib/danishDateTime';

const ATTACHMENT_KIND_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/gif': 'GIF',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/tiff': 'TIFF',
  'image/bmp': 'BMP',
};

function attachmentKindLabel(contentType: string | null | undefined): string | null {
  if (!contentType) return null;
  return ATTACHMENT_KIND_LABELS[contentType] ?? null;
}

function SegmentSignature({ signature }: { signature: BundleSignature }) {
  const at = formatMedcomDateTime(signature.at);
  if (!signature.name && !signature.role && !signature.phone && !at && !signature.organization) {
    return null;
  }
  return (
    <div className="mt-2 rounded border border-ds-line bg-ds-card p-3">
      <p className="mb-2 text-xs font-semibold text-ds-text">Underskrift</p>
      <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
        {signature.name && (
          <div>
            <dt className="text-ds-muted">Navn</dt>
            <dd className="mt-0.5 font-medium text-ds-text">{signature.name}</dd>
          </div>
        )}
        {signature.role && (
          <div>
            <dt className="text-ds-muted">Rolle</dt>
            <dd className="mt-0.5 text-ds-text">{signature.role}</dd>
          </div>
        )}
        {signature.phone && (
          <div>
            <dt className="text-ds-muted">Telefon</dt>
            <dd className="mt-0.5 text-ds-text">{signature.phone}</dd>
          </div>
        )}
        {at && (
          <div>
            <dt className="text-ds-muted">Dato</dt>
            <dd className="mt-0.5 text-ds-text">{at}</dd>
          </div>
        )}
        {signature.organization && (
          <div>
            <dt className="text-ds-muted">Organisation</dt>
            <dd className="mt-0.5 text-ds-text">
              <OrganizationLabel label={signature.organization} />
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function openAttachment(attachment: BundleAttachment, index: number): void {
  if (attachment.url && !attachment.data) {
    window.open(attachment.url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (!attachment.data) return;
  let binary: string;
  try {
    binary = atob(attachment.data);
  } catch {
    return;
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: attachment.contentType ?? 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.title ?? `attachment-${index + 1}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function CareCommunicationLetterDetails({
  bundle,
  receivedAt,
  status,
  statusLabel,
  statusTone,
  acknowledgements,
  ackTimeoutWarning,
  onExportAcknowledgement,
  onExportMessage,
}: {
  bundle: ParsedBundleContent;
  receivedAt?: string | null;
  status?: CareCommunicationStatus | null;
  statusLabel?: string | null;
  statusTone?: ReceiptStatusTone | null;
  acknowledgements?: AcknowledgementCard[];
  /** Same red badge/card treatment as Afvist — sendes igen. */
  ackTimeoutWarning?: string | null;
  onExportAcknowledgement?: (acknowledgementId: string, format: 'json' | 'xml') => void;
  onExportMessage?: (messageId: string, format: 'json' | 'xml') => void;
}) {
  const { t } = useTranslation();
  const senderOrganization = organizationOnLetter(bundle.senderOrganization, bundle.senderSor);
  const receiverOrganization = organizationOnLetter(
    bundle.receiverOrganization,
    bundle.receiverSor,
  );

  return (
    <>
      {bundle.priority ? (
        <div className="mb-4">
          <CareCommunicationPriorityChip priority={bundle.priority} variant="detail" />
        </div>
      ) : null}

      <div className="mb-4 rounded-lg border border-ds-line bg-ds-card p-5">
        <h3 className="mb-3 text-sm font-semibold text-ds-text">Afsender</h3>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          {senderOrganization && (
            <div>
              <dt className="text-ds-muted">Organisation</dt>
              <dd className="mt-0.5 font-medium text-ds-text">
                <OrganizationName
                  name={bundle.senderOrganization}
                  sorId={bundle.senderSor}
                  fallback=""
                />
              </dd>
            </div>
          )}
          {bundle.specificSenderName && (
            <div>
              <dt className="text-ds-muted">Specifik afsender</dt>
              <dd className="mt-0.5 font-medium text-ds-text">{bundle.specificSenderName}</dd>
            </div>
          )}
          {bundle.senderPractitionerName &&
            bundle.senderPractitionerName !== bundle.specificSenderName && (
              <div>
                <dt className="text-ds-muted">Kontaktperson</dt>
                <dd className="mt-0.5 font-medium text-ds-text">
                  {bundle.senderPractitionerName}
                </dd>
              </div>
            )}
          {bundle.senderRole && (
            <div>
              <dt className="text-ds-muted">Rolle / titel</dt>
              <dd className="mt-0.5 text-ds-text">{bundle.senderRole}</dd>
            </div>
          )}
          {bundle.senderPhone && (
            <div>
              <dt className="text-ds-muted">Telefon</dt>
              <dd className="mt-0.5 text-ds-text">{bundle.senderPhone}</dd>
            </div>
          )}
          {bundle.senderEmail && (
            <div>
              <dt className="text-ds-muted">Email</dt>
              <dd className="mt-0.5 text-ds-text">{bundle.senderEmail}</dd>
            </div>
          )}
        </dl>
      </div>

      <div className="mb-4 rounded-lg border border-ds-line bg-ds-card p-5">
        <h3 className="mb-3 text-sm font-semibold text-ds-text">Modtager</h3>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          {receiverOrganization && (
            <div>
              <dt className="text-ds-muted">Organisation</dt>
              <dd className="mt-0.5 font-medium text-ds-text">
                <OrganizationName
                  name={bundle.receiverOrganization}
                  sorId={bundle.receiverSor}
                  fallback=""
                />
              </dd>
            </div>
          )}
          {bundle.specificRecipientName && (
            <div>
              <dt className="text-ds-muted">Specifik modtager</dt>
              <dd className="mt-0.5 font-medium text-ds-text">{bundle.specificRecipientName}</dd>
            </div>
          )}
          {bundle.specificRecipientRole && (
            <div>
              <dt className="text-ds-muted">Rolle</dt>
              <dd className="mt-0.5 text-ds-text">{bundle.specificRecipientRole}</dd>
            </div>
          )}
        </dl>
      </div>

      <div className="mb-4 rounded-lg border border-ds-line bg-ds-card p-5">
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-ds-text">
          <User className="h-4 w-4 text-ds-muted" />
          Patient
        </h3>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-ds-muted">Navn</dt>
            <dd className="mt-0.5 font-medium text-ds-text">{bundle.patientName ?? '—'}</dd>
          </div>
          {bundle.cpr && (
            <div>
              <dt className="text-ds-muted">CPR</dt>
              <dd className="mt-0.5 font-mono text-ds-text">{bundle.cpr}</dd>
            </div>
          )}
          {bundle.xEcpr && (
            <div>
              <dt className="text-ds-muted">Erstatnings-CPR (X)</dt>
              <dd className="mt-0.5 font-mono text-ds-text">{bundle.xEcpr}</dd>
            </div>
          )}
          {bundle.dEcpr && (
            <div>
              <dt className="text-ds-muted">Erstatnings-CPR (D)</dt>
              <dd className="mt-0.5 font-mono text-ds-text">{bundle.dEcpr}</dd>
            </div>
          )}
        </dl>
      </div>

      {bundle.textSegments.length > 0 && (
        <div className="mb-4 rounded-lg border border-ds-line bg-ds-card p-5">
          <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-ds-text">
            <FileText className="h-4 w-4 text-ds-muted" />
            Beskedtekst
          </h3>
          <div className="space-y-3">
            {bundle.textSegments.map((segment, index) => (
              <div key={index}>
                {segment.timestamp && (
                  <p className="mb-1 text-xs text-ds-muted">
                    {formatMedcomDateTime(segment.timestamp) ??
                      formatDanishDateTime(segment.timestamp)}
                  </p>
                )}
                <p className="whitespace-pre-wrap rounded bg-ds-sunk p-3 text-sm text-ds-text">
                  {segment.text}
                </p>
                {segment.signature && (
                  <SegmentSignature
                    signature={segment.signature}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {bundle.attachments.length > 0 && (
        <div className="mb-4 rounded-lg border border-ds-line bg-ds-card p-5">
          <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-ds-text">
            <Paperclip className="h-4 w-4 text-ds-muted" />
            Vedhæftninger ({bundle.attachments.length})
          </h3>
          <ul className="space-y-3">
            {bundle.attachments.map((attachment, index) => {
              const kindLabel = attachmentKindLabel(attachment.contentType);
              const created = formatMedcomDateTime(attachment.creation);
              return (
                <li key={index} className="text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Paperclip className="h-3.5 w-3.5 shrink-0 text-ds-muted" />
                    {attachment.data || attachment.url ? (
                      <button
                        type="button"
                        onClick={() => openAttachment(attachment, index)}
                        className="cursor-pointer font-medium text-ds-teal underline hover:text-ds-teal-700"
                      >
                        {attachment.title ?? 'Uden navn'}
                      </button>
                    ) : (
                      <span className="font-medium text-ds-text">
                        {attachment.title ?? 'Uden navn'}
                      </span>
                    )}
                    {kindLabel && <span className="text-xs text-ds-muted">{kindLabel}</span>}
                  </div>
                  <p className="ml-6 mt-1 text-xs text-ds-muted">
                    Beskedsegment {attachment.payloadIndex}
                    {attachment.textSegmentNumber != null
                      ? ` · hører til tekst ${attachment.textSegmentNumber}`
                      : null}
                  </p>
                  {(attachment.authorName || attachment.authorPhone || created) && (
                    <dl className="ml-6 mt-1 grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-3">
                      {attachment.authorName && (
                        <div>
                          <dt className="text-ds-muted">Forfatter</dt>
                          <dd className="text-ds-text">{attachment.authorName}</dd>
                        </div>
                      )}
                      {attachment.authorPhone && (
                        <div>
                          <dt className="text-ds-muted">Telefon</dt>
                          <dd className="text-ds-text">{attachment.authorPhone}</dd>
                        </div>
                      )}
                      {created && (
                        <div>
                          <dt className="text-ds-muted">Oprettet</dt>
                          <dd className="text-ds-text">{created}</dd>
                        </div>
                      )}
                    </dl>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mb-4 rounded-lg border border-ds-line bg-ds-card p-5">
        <h3 className="mb-3 text-sm font-semibold text-ds-text">Metadata</h3>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          {bundle.priority ? (
            <div>
              <dt className="text-ds-muted">{t('communicate.field_priority')}</dt>
              <dd className="mt-0.5">
                <CareCommunicationPriorityChip priority={bundle.priority} variant="chip" />
              </dd>
            </div>
          ) : null}
          {bundle.messageTimestamp && (
            <div>
              <dt className="text-ds-muted">Besked tidsstempel</dt>
              <dd className="mt-0.5 text-ds-text">
                {formatMedcomDateTime(bundle.messageTimestamp) ??
                  formatDanishDateTime(bundle.messageTimestamp)}
              </dd>
            </div>
          )}
          {receivedAt !== undefined && (
            <div>
              <dt className="text-ds-muted">Modtaget af system</dt>
              <dd className="mt-0.5 text-ds-text">
                {receivedAt
                  ? (formatMedcomDateTime(receivedAt) ??
                    formatDanishDateTime(receivedAt))
                  : '—'}
              </dd>
            </div>
          )}
          {bundle.episodeOfCareIdentifiers.length > 0
            ? bundle.episodeOfCareIdentifiers.map((episode) => (
                <div key={`${episode.source ?? ''}:${episode.value}`}>
                  <dt className="text-ds-muted">{episodeOfCareLabel(episode.source)}</dt>
                  <dd className="mt-0.5 break-all font-mono text-xs text-ds-text">
                    {episode.value}
                  </dd>
                </div>
              ))
            : bundle.episodeOfCareId && (
                <div>
                  <dt className="text-ds-muted">Forløbs-ID</dt>
                  <dd className="mt-0.5 break-all font-mono text-xs text-ds-text">
                    {bundle.episodeOfCareId}
                  </dd>
                </div>
              )}
          {statusLabel && (
            <div>
              <dt className="text-ds-muted">Status</dt>
              <dd className="mt-0.5">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    statusTone
                      ? receiptStatusClass(statusTone)
                      : status === 'received'
                        ? 'bg-ds-green-tint text-ds-green-ink'
                        : status === 'rejected' || status === 'failed'
                          ? 'bg-ds-red-tint text-ds-red-ink'
                          : 'bg-ds-neutral-tint text-ds-text'
                  }`}
                >
                  {statusLabel}
                </span>
              </dd>
            </div>
          )}
          {ackTimeoutWarning ? (
            <div>
              <dt className="text-ds-muted">Kvittering</dt>
              <dd className="mt-0.5">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${receiptStatusClass('negative')}`}
                >
                  {ackTimeoutWarning}
                </span>
              </dd>
            </div>
          ) : null}
        </dl>
      </div>

      {ackTimeoutWarning ? (
        <div className="mb-4 rounded-lg border border-ds-red-line bg-ds-red-tint p-5">
          <h3 className="mb-2 text-sm font-semibold text-ds-text">{ackTimeoutWarning}</h3>
        </div>
      ) : null}

      {acknowledgements?.map((acknowledgement) => {
        const resendMessageId = acknowledgement.resendMessageId;
        return (
        <div
          key={acknowledgement.id}
          className={`mb-4 rounded-lg border p-5 ${
            acknowledgement.positive ? 'border-ds-green-line bg-ds-green-tint' : 'border-ds-red-line bg-ds-red-tint'
          }`}
        >
          <h3 className="mb-2 text-sm font-semibold text-ds-text">
            {acknowledgement.heading}
            {acknowledgement.positive ? ' ✓' : ' ✗'}
          </h3>
          {acknowledgement.notice && (
            <p className="mb-2 text-sm text-ds-text">{acknowledgement.notice}</p>
          )}
          {acknowledgement.resentNote && (
            <p className="mb-2 text-sm text-ds-text">{acknowledgement.resentNote}</p>
          )}
          {acknowledgement.resendHeading && (
            <p className="mb-2 text-sm font-medium text-ds-text">{acknowledgement.resendHeading}</p>
          )}
          {resendMessageId && onExportMessage && (
            <div className="mb-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="text-sm font-medium text-ds-text underline"
                onClick={() => onExportMessage(resendMessageId, 'json')}
              >
                Hent JSON
              </button>
              <button
                type="button"
                className="text-sm font-medium text-ds-text underline"
                onClick={() => onExportMessage(resendMessageId, 'xml')}
              >
                Hent XML
              </button>
            </div>
          )}
          <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-ds-muted">Type</dt>
              <dd className="mt-0.5 font-medium">{acknowledgement.label}</dd>
            </div>
            {acknowledgement.receivedAtLabel && (
              <div>
                <dt className="text-ds-muted">Modtaget</dt>
                <dd className="mt-0.5 font-medium">{acknowledgement.receivedAtLabel}</dd>
              </div>
            )}
            {acknowledgement.errorMessage && (
              <div className="sm:col-span-2">
                <dt className="text-ds-muted">Fejlbesked</dt>
                <dd className="mt-0.5 text-sm">{acknowledgement.errorMessage}</dd>
              </div>
            )}
          </dl>
          {onExportAcknowledgement && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="text-sm font-medium text-ds-text underline"
                onClick={() => onExportAcknowledgement(acknowledgement.id, 'json')}
              >
                Hent kvittering JSON
              </button>
              <button
                type="button"
                className="text-sm font-medium text-ds-text underline"
                onClick={() => onExportAcknowledgement(acknowledgement.id, 'xml')}
              >
                Hent kvittering XML
              </button>
            </div>
          )}
        </div>
        );
      })}
    </>
  );
}
